import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  FileText, Plus, Search, Filter, Download, AlertTriangle, CheckCircle2, 
  Clock, XCircle, Upload, ChevronDown, ChevronUp, Eye, Calendar, 
  Building2, Package, FileCheck, Archive, RefreshCw, X, File, Link, Unlink, Loader2,
  Home, ChevronRight, User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatDate } from '@/utils/formatters';
import { useAuth } from '@/context/AuthContext';

import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors
const STATUS_COLORS = {
  "Uploaded": "bg-blue-600",
  "Pending Review": "bg-amber-500",
  "Verified": "bg-green-600",
  "Approved": "bg-green-600",
  "Rejected": "bg-red-600",
  "Superseded": "bg-purple-600",
  "Archived": "bg-gray-600",
  "Obsolete": "bg-gray-500"
};

// Certificate Summary for Landing Page
export const CertificateSummary = ({ onViewAll }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/certificates/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching certificate stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card/50 border border-border rounded-sm p-6">
        <p className="text-muted-foreground">Loading certificate data...</p>
      </div>
    );
  }

  if (!stats) return null;

  const summaryCards = [
    { label: 'Total Certs', value: stats.total, color: 'bg-blue-600', icon: FileText },
    { label: 'Pending Review', value: stats.pending_review, color: 'bg-amber-500', icon: Clock },
    { label: 'Expiring Soon', value: stats.expiring_soon, color: 'bg-orange-500', icon: AlertTriangle },
    { label: 'Expired', value: stats.expired, color: 'bg-red-600', icon: XCircle }
  ];

  return (
    <div className="space-y-4" data-testid="certificate-summary">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-600/20 rounded-sm flex items-center justify-center">
            <FileText className="w-5 h-5 text-indigo-500" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white uppercase tracking-tight">
              Certificate Manager
            </h3>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              ISO9001 Document Control
            </p>
          </div>
        </div>
        <Button
          onClick={onViewAll}
          variant="outline"
          className="border-indigo-600 text-indigo-400 hover:bg-indigo-950/50 font-bold uppercase tracking-wide text-xs"
          data-testid="view-certificates-btn"
        >
          <Eye className="w-4 h-4 mr-2" />
          View All
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          const isAlert = card.label !== 'Total Certs' && card.value > 0;
          return (
            <div
              key={card.label}
              className={`${card.color}/20 border ${card.color.replace('bg-', 'border-')} rounded-sm p-3 ${
                isAlert ? 'animate-pulse' : ''
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${card.color.replace('bg-', 'text-')}`} />
                <p className="text-xs text-muted-foreground uppercase tracking-wider truncate">
                  {card.label}
                </p>
              </div>
              <p className="text-2xl font-bold text-white">{card.value}</p>
            </div>
          );
        })}
      </div>

      {/* Recent Uploads */}
      {stats.recent_uploads && stats.recent_uploads.length > 0 && (
        <div className="border border-border rounded-sm">
          <div className="bg-muted/30 px-4 py-2 border-b border-border">
            <p className="text-xs text-muted-foreground uppercase font-bold">Recent Uploads</p>
          </div>
          <div className="divide-y divide-border">
            {stats.recent_uploads.slice(0, 3).map((cert) => (
              <div key={cert.certificate_id} className="px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <File className="w-4 h-4 text-indigo-400" />
                  <div>
                    <p className="text-white text-sm font-medium truncate max-w-[200px]">
                      {cert.original_file_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {cert.certificate_type} • {formatDate(cert.upload_date)}
                    </p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-sm font-bold ${STATUS_COLORS[cert.status]} text-white`}>
                  {cert.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alerts */}
      {(stats.pending_review > 0 || stats.expiring_soon > 0 || stats.missing_metadata > 0) && (
        <div className="flex flex-wrap gap-2">
          {stats.pending_review > 0 && (
            <div className="bg-amber-950/50 border border-amber-600 rounded-sm px-3 py-1 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="text-amber-400 text-sm font-bold">{stats.pending_review} AWAITING REVIEW</span>
            </div>
          )}
          {stats.expiring_soon > 0 && (
            <div className="bg-orange-950/50 border border-orange-600 rounded-sm px-3 py-1 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              <span className="text-orange-400 text-sm font-bold">{stats.expiring_soon} EXPIRING</span>
            </div>
          )}
          {stats.missing_metadata > 0 && (
            <div className="bg-red-950/50 border border-red-600 rounded-sm px-3 py-1 flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-red-400 text-sm font-bold">{stats.missing_metadata} INCOMPLETE</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Traceability Links Manager Component
const TraceabilityLinksManager = ({ certificate, onUpdate }) => {
  const [linkType, setLinkType] = useState('work_order');
  const [reference, setReference] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [availableItems, setAvailableItems] = useState({ workOrders: [], parts: [], ncrs: [], equipment: [] });
  const [loadingItems, setLoadingItems] = useState(false);

  // Fetch available items for linking
  useEffect(() => {
    fetchAvailableItems();
  }, []);

  const fetchAvailableItems = async () => {
    setLoadingItems(true);
    try {
      const [woRes, partsRes, ncrsRes, eqRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/works-orders`),
        axios.get(`${BACKEND_URL}/api/parts`),
        axios.get(`${BACKEND_URL}/api/ncrs`),
        axios.get(`${BACKEND_URL}/api/equipment`)
      ]);
      setAvailableItems({
        workOrders: woRes.data || [],
        parts: partsRes.data || [],
        ncrs: ncrsRes.data || [],
        equipment: eqRes.data || []
      });
    } catch (error) {
      console.error('Error fetching available items:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleAddLink = async () => {
    if (!reference.trim()) {
      toast.error('Please enter or select a reference');
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${BACKEND_URL}/api/certificates/${certificate.certificate_id}/link?added_by=User`, {
        link_type: linkType,
        reference: reference.trim()
      });
      toast.success(`Link added: ${reference}`);
      setReference('');
      onUpdate();
    } catch (error) {
      console.error('Error adding link:', error);
      toast.error(error.response?.data?.detail || 'Failed to add link');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveLink = async (type, ref) => {
    setRemoving(`${type}-${ref}`);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/certificates/${certificate.certificate_id}/link?link_type=${type}&reference=${encodeURIComponent(ref)}&removed_by=User`
      );
      toast.success(`Link removed: ${ref}`);
      onUpdate();
    } catch (error) {
      console.error('Error removing link:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove link');
    } finally {
      setRemoving(null);
    }
  };

  const linkTypeConfig = {
    work_order: { label: 'Work Orders', icon: Package, color: 'purple', items: availableItems.workOrders, idField: 'order_number' },
    part: { label: 'Parts', icon: Building2, color: 'green', items: availableItems.parts, idField: 'part_number' },
    ncr: { label: 'NCRs', icon: AlertTriangle, color: 'red', items: availableItems.ncrs, idField: 'ncr_number' },
    equipment: { label: 'Equipment', icon: FileCheck, color: 'blue', items: availableItems.equipment, idField: 'equipment_id' }
  };

  const getLinkedItems = (type) => {
    const fieldMap = {
      work_order: 'linked_work_orders',
      part: 'linked_parts',
      ncr: 'linked_ncrs',
      equipment: 'linked_equipment'
    };
    return certificate[fieldMap[type]] || [];
  };

  return (
    <div className="space-y-4">
      {/* Add New Link Section */}
      <div className="bg-purple-950/20 border border-purple-800 rounded-sm p-4">
        <h4 className="text-purple-400 font-bold uppercase tracking-wide mb-3 text-sm flex items-center gap-2">
          <Link className="w-4 h-4" />
          Link to Records
        </h4>
        
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground uppercase mb-1">Link Type</Label>
            <Select value={linkType} onValueChange={setLinkType}>
              <SelectTrigger className="bg-muted/50 border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="work_order">Work Order</SelectItem>
                <SelectItem value="part">Part</SelectItem>
                <SelectItem value="ncr">NCR</SelectItem>
                <SelectItem value="equipment">Equipment</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex-[2]">
            <Label className="text-xs text-muted-foreground uppercase mb-1">Reference</Label>
            <div className="flex gap-2">
              {loadingItems ? (
                <div className="flex-1 flex items-center justify-center py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Select value={reference} onValueChange={setReference}>
                  <SelectTrigger className="bg-muted/50 border-border flex-1" data-testid="link-reference-select">
                    <SelectValue placeholder={`Select ${linkTypeConfig[linkType].label.slice(0, -1)}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {linkTypeConfig[linkType].items.map((item) => {
                      const id = item[linkTypeConfig[linkType].idField];
                      const linked = getLinkedItems(linkType);
                      const isLinked = linked.includes(id);
                      return (
                        <SelectItem 
                          key={id} 
                          value={id}
                          disabled={isLinked}
                        >
                          {id} {isLinked && '(already linked)'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
              <Button
                onClick={handleAddLink}
                disabled={!reference || adding}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="add-link-btn"
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Linked Records Display */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(linkTypeConfig).map(([type, config]) => {
          const Icon = config.icon;
          const linked = getLinkedItems(type);
          
          return (
            <div 
              key={type}
              className={`bg-${config.color}-950/20 border border-${config.color}-800 rounded-sm p-3`}
              style={{
                backgroundColor: config.color === 'purple' ? 'rgba(88, 28, 135, 0.2)' :
                                config.color === 'green' ? 'rgba(20, 83, 45, 0.2)' :
                                config.color === 'red' ? 'rgba(127, 29, 29, 0.2)' :
                                'rgba(30, 58, 138, 0.2)',
                borderColor: config.color === 'purple' ? 'rgb(107, 33, 168)' :
                             config.color === 'green' ? 'rgb(22, 101, 52)' :
                             config.color === 'red' ? 'rgb(153, 27, 27)' :
                             'rgb(30, 64, 175)'
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 text-${config.color}-400`} style={{
                  color: config.color === 'purple' ? 'rgb(192, 132, 252)' :
                         config.color === 'green' ? 'rgb(74, 222, 128)' :
                         config.color === 'red' ? 'rgb(248, 113, 113)' :
                         'rgb(96, 165, 250)'
                }} />
                <span className="text-xs text-muted-foreground uppercase font-bold">
                  {config.label} ({linked.length})
                </span>
              </div>
              
              {linked.length > 0 ? (
                <div className="space-y-1">
                  {linked.map((ref) => (
                    <div 
                      key={ref}
                      className="flex items-center justify-between bg-muted/30 rounded px-2 py-1"
                    >
                      <span className="text-white font-mono text-sm">{ref}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveLink(type, ref)}
                        disabled={removing === `${type}-${ref}`}
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-950/50"
                        data-testid={`remove-link-${type}-${ref}`}
                      >
                        {removing === `${type}-${ref}` ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Unlink className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-xs">No linked {config.label.toLowerCase()}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main Certificate Manager Module
const CertificateManager = ({ onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const handleBack = () => {
    if (onClose) onClose();
    else navigate('/');
  };
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  
  // Upload states
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // Upload form data
  const [uploadData, setUploadData] = useState({
    certificate_type: '',
    certificate_number: '',
    work_order_reference: '',
    job_card_reference: '',
    ncr_reference: '',
    batch_number: '',
    heat_number: '',
    lot_number: '',
    equipment_reference: '',
    part_number: '',
    part_description: '',
    material_grade: '',
    supplier: '',
    customer: '',
    issue_date: '',
    expiry_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [certificates, searchQuery, typeFilter, statusFilter]);

  const fetchData = async () => {
    try {
      const [certsRes, configRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/certificates`),
        axios.get(`${BACKEND_URL}/api/certificates/config`),
        axios.get(`${BACKEND_URL}/api/certificates/stats`)
      ]);
      setCertificates(certsRes.data);
      setConfig(configRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load certificate data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...certificates];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(cert => 
        cert.certificate_id?.toLowerCase().includes(query) ||
        cert.certificate_number?.toLowerCase().includes(query) ||
        cert.original_file_name?.toLowerCase().includes(query) ||
        cert.part_number?.toLowerCase().includes(query) ||
        cert.supplier?.toLowerCase().includes(query) ||
        cert.customer?.toLowerCase().includes(query) ||
        cert.work_order_reference?.toLowerCase().includes(query)
      );
    }
    
    if (typeFilter) {
      filtered = filtered.filter(cert => cert.certificate_type === typeFilter);
    }
    
    if (statusFilter) {
      filtered = filtered.filter(cert => cert.status === statusFilter);
    }
    
    setFilteredCertificates(filtered);
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/pdf') {
        setUploadFile(file);
      } else {
        toast.error('Only PDF files are allowed');
      }
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type === 'application/pdf') {
        setUploadFile(file);
      } else {
        toast.error('Only PDF files are allowed');
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadFile) {
      toast.error('Please select a PDF file');
      return;
    }
    
    if (!uploadData.certificate_type) {
      toast.error('Please select certificate type');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      // Add all metadata fields
      Object.entries(uploadData).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      formData.append('uploaded_by', 'User');
      
      const response = await axios.post(`${BACKEND_URL}/api/certificates/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Certificate ${response.data.certificate_id} uploaded successfully`);
      setUploadDialogOpen(false);
      resetUploadForm();
      fetchData();
    } catch (error) {
      console.error('Error uploading certificate:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload certificate');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadData({
      certificate_type: '',
      certificate_number: '',
      work_order_reference: '',
      job_card_reference: '',
      ncr_reference: '',
      batch_number: '',
      heat_number: '',
      lot_number: '',
      equipment_reference: '',
      part_number: '',
      part_description: '',
      material_grade: '',
      supplier: '',
      customer: '',
      issue_date: '',
      expiry_date: '',
      notes: ''
    });
  };

  const handleCertificateClick = async (cert) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/certificates/${cert.certificate_id}`);
      setSelectedCertificate(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Error fetching certificate:', error);
      toast.error('Failed to load certificate details');
    }
  };

  const handleDownload = async (cert) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/certificates/${cert.certificate_id}/download?downloaded_by=User`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', cert.original_file_name || `${cert.certificate_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded');
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
    }
  };

  const handleVerify = async (approved) => {
    if (!selectedCertificate) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/certificates/${selectedCertificate.certificate_id}/verify?verified_by=User&approved=${approved}`
      );
      toast.success(`Certificate ${approved ? 'approved' : 'rejected'}`);
      setVerifyDialogOpen(false);
      
      // Refresh
      const response = await axios.get(`${BACKEND_URL}/api/certificates/${selectedCertificate.certificate_id}`);
      setSelectedCertificate(response.data);
      fetchData();
    } catch (error) {
      console.error('Error verifying certificate:', error);
      toast.error('Failed to verify certificate');
    }
  };

  const handleArchive = async () => {
    if (!selectedCertificate) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/certificates/${selectedCertificate.certificate_id}/archive?archived_by=User`
      );
      toast.success('Certificate archived');
      setDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error archiving certificate:', error);
      toast.error('Failed to archive certificate');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden text-white" data-testid="certificate-manager">
      {/* Honeycomb Background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-cert" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 0L56 16.5V49.5L28 66L0 49.5V16.5L28 0Z" fill="none" stroke="rgba(99, 102, 241, 0.15)" strokeWidth="1"/>
              <path d="M28 66L56 82.5V115.5L28 132L0 115.5V82.5L28 66Z" fill="none" stroke="rgba(99, 102, 241, 0.15)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-cert)" />
        </svg>
      </div>
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(99, 102, 241, 0.06) 0%, transparent 70%)' }} />

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-950/90 backdrop-blur-xl border-b border-indigo-500/20">
        <div className="h-full max-w-[1920px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={handleBack} className="text-slate-400 hover:text-indigo-400 hover:bg-indigo-500/10" data-testid="back-btn">
              <Home className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center">
                <FileText className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide">Certificate Manager</h1>
                <p className="text-xs text-slate-500">ISO9001 Document & Record Control</p>
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-slate-500">Home</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-indigo-400 font-medium">Certificates</span>
          </nav>
          <div className="flex items-center gap-4">
            <Button onClick={() => setUploadDialogOpen(true)} className="bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400 border border-indigo-500/50 hover:shadow-[0_0_20px_rgba(99,102,241,0.3)]" data-testid="upload-certificate-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload Certificate
            </Button>
            <div className="hidden sm:flex items-center gap-2 text-sm">
              <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
                <User className="w-4 h-4 text-indigo-400" />
              </div>
              <span className="text-slate-400">{user?.name || user?.email?.split('@')[0]}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[1600px] mx-auto space-y-6">

      {/* Summary Stats */}
      {stats && (
        <div className="bg-slate-900/60 backdrop-blur-sm border border-indigo-500/10 rounded-lg px-6 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Total</p>
              <p className="text-2xl font-bold font-mono text-white">{stats.total}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Pending Review</p>
              <p className={`text-2xl font-bold font-mono text-amber-400 ${stats.pending_review > 0 ? 'animate-pulse' : ''}`}>{stats.pending_review}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Approved</p>
              <p className="text-2xl font-bold font-mono text-green-400">{stats.by_status?.Approved || 0}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Expiring Soon</p>
              <p className={`text-2xl font-bold font-mono text-orange-400 ${stats.expiring_soon > 0 ? 'animate-pulse' : ''}`}>{stats.expiring_soon}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Expired</p>
              <p className={`text-2xl font-bold font-mono text-red-400 ${stats.expired > 0 ? 'animate-pulse' : ''}`}>{stats.expired}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Archived</p>
              <p className="text-2xl font-bold font-mono text-slate-400">{stats.by_status?.Archived || 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-slate-900/40 backdrop-blur-sm border border-indigo-500/10 rounded-lg px-4 py-3">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search certificates..."
              className="pl-10 bg-slate-950/50 border-slate-800 text-white placeholder:text-slate-600 focus:border-indigo-500/50"
              data-testid="certificate-search-input"
            />
          </div>
          
          <Select value={typeFilter || "all"} onValueChange={(val) => setTypeFilter(val === "all" ? "" : val)}>
            <SelectTrigger className="w-[160px] bg-slate-950/50 border-slate-800">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {config?.types?.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
            <SelectTrigger className="w-[140px] bg-slate-950/50 border-slate-800">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {config?.statuses?.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            onClick={() => { setTypeFilter(''); setStatusFilter(''); setSearchQuery(''); }}
            className="border-slate-700 hover:border-indigo-500/50"
          >
            Clear Filters
          </Button>
          
          <Button variant="ghost" onClick={fetchData} className="text-slate-400 hover:text-indigo-400 hover:bg-indigo-500/10">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Certificate List */}
      <div className="bg-slate-900/40 backdrop-blur-md border border-indigo-500/10 rounded-xl overflow-hidden">
        <div className="bg-slate-900/60 border-b border-slate-800 px-4 py-3">
          <p className="text-sm text-slate-400">
            Showing {filteredCertificates.length} of {certificates.length} certificates
          </p>
        </div>
        
        <div className="divide-y divide-slate-800/50 max-h-[600px] overflow-y-auto">
          {filteredCertificates.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-slate-900/60 border border-slate-800 flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-slate-600" />
              </div>
              <p className="text-slate-400 mb-4">No certificates found</p>
              <Button
                onClick={() => setUploadDialogOpen(true)}
                className="bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400 border border-indigo-500/50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload First Certificate
              </Button>
            </div>
          ) : (
            filteredCertificates.map((cert) => (
              <div
                key={cert.certificate_id}
                className="p-4 hover:bg-indigo-500/5 cursor-pointer transition-colors"
                onClick={() => handleCertificateClick(cert)}
                data-testid={`certificate-row-${cert.certificate_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 bg-indigo-500/10 border border-indigo-500/30 rounded-lg flex items-center justify-center">
                      <File className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-white font-bold">{cert.certificate_id}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-sm font-bold ${STATUS_COLORS[cert.status]} text-white`}>
                          {cert.status}
                        </span>
                        {cert.is_expired && (
                          <span className="text-xs px-2 py-0.5 rounded-sm font-bold bg-red-900 text-red-200">
                            EXPIRED
                          </span>
                        )}
                        {cert.is_expiring_soon && !cert.is_expired && (
                          <span className="text-xs px-2 py-0.5 rounded-sm font-bold bg-orange-900 text-orange-200">
                            EXPIRING
                          </span>
                        )}
                      </div>
                      <p className="text-white text-sm truncate">{cert.original_file_name}</p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                        <span>{cert.certificate_type}</span>
                        {cert.certificate_number && <span>#{cert.certificate_number}</span>}
                        {cert.supplier && <span>{cert.supplier}</span>}
                        {cert.work_order_reference && <span>WO: {cert.work_order_reference}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(cert);
                      }}
                      className="text-indigo-400 hover:text-indigo-300"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <div className="text-right text-xs text-muted-foreground">
                      <p>{formatDate(cert.upload_date)}</p>
                      <p className="text-muted-foreground">{formatFileSize(cert.file_size)}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

        </div>
      </main>

      {/* Upload Certificate Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="bg-slate-950 border-indigo-500/30 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="upload-certificate-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <Upload className="w-5 h-5 text-indigo-400" />
              Upload Certificate
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Upload a PDF certificate and fill in the metadata.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpload} className="space-y-6">
            {/* File Upload Area */}
            <div
              className={`border-2 border-dashed rounded-sm p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-indigo-500 bg-indigo-950/30' 
                  : uploadFile 
                    ? 'border-green-600 bg-green-950/20' 
                    : 'border-border hover:border-indigo-500'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {uploadFile ? (
                <div className="flex items-center justify-center gap-4">
                  <File className="w-12 h-12 text-green-500" />
                  <div className="text-left">
                    <p className="text-white font-bold">{uploadFile.name}</p>
                    <p className="text-sm text-muted-foreground">{formatFileSize(uploadFile.size)}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setUploadFile(null)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-white font-bold mb-2">Drop PDF file here or click to browse</p>
                  <p className="text-sm text-muted-foreground mb-4">Only PDF files are accepted</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-indigo-600 text-indigo-400"
                  >
                    Select File
                  </Button>
                </>
              )}
            </div>

            {/* Certificate Type */}
            <div>
              <Label className="text-white uppercase tracking-wider text-xs">Certificate Type *</Label>
              <Select
                value={uploadData.certificate_type}
                onValueChange={(val) => setUploadData(prev => ({ ...prev, certificate_type: val }))}
              >
                <SelectTrigger className="bg-muted/50 border-border mt-2" data-testid="cert-type-select">
                  <SelectValue placeholder="Select certificate type" />
                </SelectTrigger>
                <SelectContent>
                  {config?.types?.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Certificate Details */}
            <div className="border border-indigo-800 rounded-sm p-4 bg-indigo-950/20">
              <h4 className="text-indigo-400 font-bold uppercase tracking-wide mb-3 text-sm">
                Certificate Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Certificate Number</Label>
                  <Input
                    value={uploadData.certificate_number}
                    onChange={(e) => setUploadData(prev => ({ ...prev, certificate_number: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="External reference number"
                    data-testid="cert-number-input"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Issue Date</Label>
                  <Input
                    type="date"
                    value={uploadData.issue_date}
                    onChange={(e) => setUploadData(prev => ({ ...prev, issue_date: e.target.value }))}
                    className="bg-muted/50 border-border"
                  />
                </div>
                {uploadData.certificate_type === 'Calibration Certificate' && (
                  <>
                    <div>
                      <Label className="text-white uppercase tracking-wider text-xs">Expiry Date</Label>
                      <Input
                        type="date"
                        value={uploadData.expiry_date}
                        onChange={(e) => setUploadData(prev => ({ ...prev, expiry_date: e.target.value }))}
                        className="bg-muted/50 border-border"
                      />
                    </div>
                    <div>
                      <Label className="text-white uppercase tracking-wider text-xs">Equipment Reference</Label>
                      <Input
                        value={uploadData.equipment_reference}
                        onChange={(e) => setUploadData(prev => ({ ...prev, equipment_reference: e.target.value }))}
                        className="bg-muted/50 border-border"
                        placeholder="e.g., EQ-2026-0001"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Related Records */}
            <div className="border border-purple-800 rounded-sm p-4 bg-purple-950/20">
              <h4 className="text-purple-400 font-bold uppercase tracking-wide mb-3 text-sm">
                Related Records
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Work Order</Label>
                  <Input
                    value={uploadData.work_order_reference}
                    onChange={(e) => setUploadData(prev => ({ ...prev, work_order_reference: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="Work order number"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Batch Number</Label>
                  <Input
                    value={uploadData.batch_number}
                    onChange={(e) => setUploadData(prev => ({ ...prev, batch_number: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="Batch/Lot number"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Heat Number</Label>
                  <Input
                    value={uploadData.heat_number}
                    onChange={(e) => setUploadData(prev => ({ ...prev, heat_number: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="Heat number"
                  />
                </div>
              </div>
            </div>

            {/* Product Info */}
            <div className="border border-green-800 rounded-sm p-4 bg-green-950/20">
              <h4 className="text-green-400 font-bold uppercase tracking-wide mb-3 text-sm">
                Product Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Part Number</Label>
                  <Input
                    value={uploadData.part_number}
                    onChange={(e) => setUploadData(prev => ({ ...prev, part_number: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="Part number"
                  />
                </div>
                <div>
                  <Label className="text-white uppercase tracking-wider text-xs">Material Grade</Label>
                  <Input
                    value={uploadData.material_grade}
                    onChange={(e) => setUploadData(prev => ({ ...prev, material_grade: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="e.g., EN19, 4140"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-white uppercase tracking-wider text-xs">Part Description</Label>
                  <Input
                    value={uploadData.part_description}
                    onChange={(e) => setUploadData(prev => ({ ...prev, part_description: e.target.value }))}
                    className="bg-muted/50 border-border"
                    placeholder="Part description"
                  />
                </div>
              </div>
            </div>

            {/* Parties */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Supplier</Label>
                <Input
                  value={uploadData.supplier}
                  onChange={(e) => setUploadData(prev => ({ ...prev, supplier: e.target.value }))}
                  className="bg-muted/50 border-border"
                  placeholder="Supplier name"
                />
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs">Customer</Label>
                <Input
                  value={uploadData.customer}
                  onChange={(e) => setUploadData(prev => ({ ...prev, customer: e.target.value }))}
                  className="bg-muted/50 border-border"
                  placeholder="Customer name"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-white uppercase tracking-wider text-xs">Notes</Label>
              <Textarea
                value={uploadData.notes}
                onChange={(e) => setUploadData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-muted/50 border-border min-h-[60px]"
                placeholder="Additional notes..."
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setUploadDialogOpen(false);
                  resetUploadForm();
                }}
                className="border-border"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!uploadFile || !uploadData.certificate_type || uploading}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold uppercase tracking-wide"
                data-testid="submit-upload-btn"
              >
                {uploading ? 'Uploading...' : 'Upload Certificate'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Certificate Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="certificate-detail-dialog">
          {selectedCertificate && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-500" />
                    {selectedCertificate.certificate_id}
                  </DialogTitle>
                  <span className={`text-xs px-2 py-1 rounded-sm font-bold ${STATUS_COLORS[selectedCertificate.status]} text-white`}>
                    {selectedCertificate.status}
                  </span>
                </div>
              </DialogHeader>

              <Tabs defaultValue="details" className="mt-4">
                <TabsList className="bg-muted/30 border border-border">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="traceability">Traceability</TabsTrigger>
                  <TabsTrigger value="audit">Audit Trail</TabsTrigger>
                </TabsList>

                {/* Details Tab */}
                <TabsContent value="details" className="space-y-4 mt-4">
                  {/* File Info */}
                  <div className="bg-indigo-950/20 border border-indigo-800 rounded-sm p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <File className="w-10 h-10 text-indigo-400" />
                        <div>
                          <p className="text-white font-bold">{selectedCertificate.original_file_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedCertificate.certificate_type} • {formatFileSize(selectedCertificate.file_size)}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDownload(selectedCertificate)}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="download-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  </div>

                  {/* Certificate Details */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Certificate Number</p>
                      <p className="text-white font-bold">{selectedCertificate.certificate_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Issue Date</p>
                      <p className="text-white font-bold">
                        {selectedCertificate.issue_date ? formatDate(selectedCertificate.issue_date) : 'N/A'}
                      </p>
                    </div>
                    {selectedCertificate.expiry_date && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Expiry Date</p>
                        <p className={`font-bold ${
                          selectedCertificate.is_expired ? 'text-red-400' :
                          selectedCertificate.is_expiring_soon ? 'text-orange-400' : 'text-green-400'
                        }`}>
                          {formatDate(selectedCertificate.expiry_date)}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Uploaded By</p>
                      <p className="text-white">{selectedCertificate.uploaded_by}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Upload Date</p>
                      <p className="text-white">{formatDate(selectedCertificate.upload_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Retention Until</p>
                      <p className="text-white">{formatDate(selectedCertificate.retention_until)}</p>
                    </div>
                  </div>

                  {/* Verification Status */}
                  {selectedCertificate.verified_by && (
                    <div className="bg-green-950/20 border border-green-800 rounded-sm p-4">
                      <h4 className="text-green-400 font-bold uppercase text-sm mb-2">Verification</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Verified By</p>
                          <p className="text-white font-bold">{selectedCertificate.verified_by}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Verification Date</p>
                          <p className="text-white">{formatDate(selectedCertificate.verification_date)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Product & Parties */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-muted/10 p-4 rounded-sm border border-border">
                    {selectedCertificate.supplier && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Supplier</p>
                        <p className="text-white font-bold">{selectedCertificate.supplier}</p>
                      </div>
                    )}
                    {selectedCertificate.customer && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Customer</p>
                        <p className="text-white font-bold">{selectedCertificate.customer}</p>
                      </div>
                    )}
                    {selectedCertificate.part_number && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Part Number</p>
                        <p className="text-white font-mono">{selectedCertificate.part_number}</p>
                      </div>
                    )}
                    {selectedCertificate.material_grade && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Material Grade</p>
                        <p className="text-white">{selectedCertificate.material_grade}</p>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  {selectedCertificate.notes && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase mb-2">Notes</p>
                      <div className="bg-muted/20 border border-border rounded-sm p-4">
                        <p className="text-white whitespace-pre-wrap">{selectedCertificate.notes}</p>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                    {(selectedCertificate.status === 'Uploaded' || selectedCertificate.status === 'Pending Review') && (
                      <Button
                        onClick={() => setVerifyDialogOpen(true)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="verify-btn"
                      >
                        <FileCheck className="w-4 h-4 mr-2" />
                        Verify / Approve
                      </Button>
                    )}
                    {selectedCertificate.status !== 'Archived' && selectedCertificate.status !== 'Superseded' && (
                      <Button
                        variant="outline"
                        onClick={handleArchive}
                        className="border-gray-600 text-gray-400 hover:bg-gray-950/50"
                      >
                        <Archive className="w-4 h-4 mr-2" />
                        Archive
                      </Button>
                    )}
                  </div>
                </TabsContent>

                {/* Traceability Tab */}
                <TabsContent value="traceability" className="space-y-4 mt-4">
                  {/* Link Management Section */}
                  <TraceabilityLinksManager 
                    certificate={selectedCertificate} 
                    onUpdate={async () => {
                      const response = await axios.get(`${BACKEND_URL}/api/certificates/${selectedCertificate.certificate_id}`);
                      setSelectedCertificate(response.data);
                      fetchData();
                    }}
                  />

                  {/* Legacy Single References */}
                  <div className="border border-border rounded-sm p-4 mt-4">
                    <h4 className="text-white font-bold uppercase text-sm mb-3">Legacy References</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {selectedCertificate.work_order_reference && (
                        <div className="bg-muted/20 border border-border rounded-sm p-3">
                          <p className="text-xs text-muted-foreground uppercase">Work Order</p>
                          <p className="text-white font-mono font-bold">{selectedCertificate.work_order_reference}</p>
                        </div>
                      )}
                      {selectedCertificate.job_card_reference && (
                        <div className="bg-muted/20 border border-border rounded-sm p-3">
                          <p className="text-xs text-muted-foreground uppercase">Job Card</p>
                          <p className="text-white font-mono font-bold">{selectedCertificate.job_card_reference}</p>
                        </div>
                      )}
                      {selectedCertificate.ncr_reference && (
                        <div className="bg-muted/20 border border-border rounded-sm p-3">
                          <p className="text-xs text-muted-foreground uppercase">NCR Reference</p>
                          <p className="text-white font-mono font-bold">{selectedCertificate.ncr_reference}</p>
                        </div>
                      )}
                      {selectedCertificate.batch_number && (
                        <div className="bg-muted/20 border border-border rounded-sm p-3">
                          <p className="text-xs text-muted-foreground uppercase">Batch Number</p>
                          <p className="text-white font-mono font-bold">{selectedCertificate.batch_number}</p>
                        </div>
                      )}
                      {selectedCertificate.heat_number && (
                        <div className="bg-muted/20 border border-border rounded-sm p-3">
                          <p className="text-xs text-muted-foreground uppercase">Heat Number</p>
                          <p className="text-white font-mono font-bold">{selectedCertificate.heat_number}</p>
                        </div>
                      )}
                      {selectedCertificate.lot_number && (
                        <div className="bg-muted/20 border border-border rounded-sm p-3">
                          <p className="text-xs text-muted-foreground uppercase">Lot Number</p>
                          <p className="text-white font-mono font-bold">{selectedCertificate.lot_number}</p>
                        </div>
                      )}
                      {selectedCertificate.equipment_reference && (
                        <div className="bg-muted/20 border border-border rounded-sm p-3">
                          <p className="text-xs text-muted-foreground uppercase">Equipment</p>
                          <p className="text-white font-mono font-bold">{selectedCertificate.equipment_reference}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Version History */}
                  {(selectedCertificate.previous_versions?.length > 0 || selectedCertificate.superseded_by) && (
                    <div className="border border-border rounded-sm p-4">
                      <h4 className="text-white font-bold uppercase text-sm mb-3">Version History</h4>
                      <p className="text-muted-foreground text-sm">Revision: {selectedCertificate.revision}</p>
                      {selectedCertificate.previous_versions?.length > 0 && (
                        <p className="text-muted-foreground text-sm">
                          Previous versions: {selectedCertificate.previous_versions.join(', ')}
                        </p>
                      )}
                      {selectedCertificate.superseded_by && (
                        <p className="text-amber-400 text-sm">
                          Superseded by: {selectedCertificate.superseded_by}
                        </p>
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* Audit Trail Tab */}
                <TabsContent value="audit" className="mt-4">
                  <div className="border border-border rounded-sm">
                    <div className="bg-muted/30 px-4 py-2 border-b border-border">
                      <h4 className="text-sm font-bold text-white uppercase">Audit Trail</h4>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      {selectedCertificate.audit_trail?.length > 0 ? (
                        selectedCertificate.audit_trail.slice().reverse().map((entry, idx) => (
                          <div key={idx} className="p-3 border-b border-border last:border-b-0 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-white font-bold">{entry.action}</span>
                              <span className="text-xs text-muted-foreground">{formatDate(entry.timestamp)}</span>
                            </div>
                            <p className="text-muted-foreground text-xs mt-1">{entry.user}: {entry.details}</p>
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-muted-foreground">
                          No audit history
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Footer */}
              <div className="flex justify-end pt-4 border-t border-border mt-4">
                <Button
                  variant="outline"
                  onClick={() => setDetailDialogOpen(false)}
                  className="border-border"
                >
                  Close
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Verify Dialog */}
      <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="verify-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-green-500" />
              Verify Certificate
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-muted-foreground">
              Review and verify certificate <span className="text-white font-bold">{selectedCertificate?.certificate_id}</span>
            </p>
            
            {selectedCertificate && (
              <div className="bg-muted/20 border border-border rounded-sm p-4">
                <p className="text-white font-bold">{selectedCertificate.original_file_name}</p>
                <p className="text-sm text-muted-foreground">{selectedCertificate.certificate_type}</p>
                {selectedCertificate.certificate_number && (
                  <p className="text-sm text-muted-foreground">#{selectedCertificate.certificate_number}</p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => handleVerify(false)}
                className="border-red-600 text-red-400 hover:bg-red-950/50"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button
                onClick={() => handleVerify(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-bold"
                data-testid="approve-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CertificateManager;
